/**
 * `<SymbolDialog>` — the symbol search, as one dialog for the whole app.
 *
 * It used to be rendered inside `<SymbolSearch>`, which is mounted once per
 * chart toolbar — so a two-chart layout had two of these, and app-wide typing
 * needed a module-scope subscriber set and a prop to nominate which one
 * answered. **Nothing in this dialog is per-chart.** What it ranks, filters and
 * renders is the same whichever chart is selected; the only per-chart fact is
 * where the answer goes, and that is the caller's to know. So the app renders
 * one and hands the pick to whichever chart it decided was the target.
 *
 * ## It opens before the index arrives
 *
 * The universe is ~2.7MB and deliberately not fetched until something needs it,
 * so an open on a cold page has nothing to list yet. It **opens anyway**, with
 * an empty list and a footer that says why. The alternative — wait for the rows
 * and open when they land — is what the old code did, and a keystroke that
 * appears to do nothing for a second is the single loudest complaint this
 * feature has had. The query field is live immediately, so what the user types
 * while waiting is not lost.
 *
 * ## Enter commits only a symbol this account may chart
 *
 * The index is narrowed to the user's universe on the server, so it *is* the
 * set of symbols their plan covers — and since 2026-09-26 the server no longer
 * checks a chart request's symbol at all; this dialog is where that is decided
 * (`lib/symbol-access.ts`, `docs/plans/auth-hot-path.md` §2). Enter on a
 * symbol, or a ratio leg, that the index does not hold **keeps the dialog
 * open** and says why, under the box: `No symbol “XYZ”.` or `“XYZ” isn't in
 * your plan.` Esc closes it and the chart keeps its symbol.
 *
 * It used to commit the raw text anyway, on the argument that the index is
 * what we know about rather than what exists. With the index as the plan's
 * universe, that path was a way round the plan.
 *
 * Arrowing up past the first row still lands on "nothing highlighted" rather
 * than wrapping, so Enter commits what was typed rather than a row the user
 * has not looked at.
 *
 * ## Why it is `position: fixed`
 *
 * The same reason `Menu` in `GroupBar.tsx` is, and `FieldRef` in screener-grid:
 * an absolutely-positioned overlay is clipped by the nearest ancestor with
 * `overflow: hidden`, and the toolbar sits inside one. That has bitten this
 * codebase twice. `fixed` plus a z-index above the menus (1000) takes the
 * dialog out of every clipping context there is.
 *
 * Colours are `var(--qf-*)` only. `useTheme()` exists for SciChart and AG Grid,
 * which cannot read CSS; this is chrome, and chrome inherits. The one literal
 * is the backdrop scrim, which follows the existing box-shadows: a darkening is
 * theme-independent and there is no token that means "scrim".
 */

import { useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { mintUlid } from "@qufolio/model";

import {
  activeToken,
  committedSymbol,
  isComposite,
  parseComposite,
  replaceActiveToken,
} from "../lib/composite";
import { symbolResolver, useSymbolIndex, type SymbolRow } from "../lib/symbol-index";
import { firstOutside, refusalFor, refusalMessage } from "../lib/symbol-access";
import { symbolsConfigured } from "../api/symbols";
import { MAX_ROWS, rankSymbols } from "../lib/symbol-rank";
import {
  colourToken,
  createList,
  defaultList,
  listsFor,
  removeFromAll,
  toggleSymbols,
  useTrackLists,
  type TrackList,
} from "../lib/track-lists";
import { NewListDialog } from "./NewListDialog";
import { TrackListMenu } from "./TrackListMenu";
import { available } from "../lib/features";
import { useTokenResolver } from "./theme";
import {
  formatCap,
  formatPrice,
  bareSymbol,
  marketByCode,
  marketForSymbol,
  type MarketInfo,
} from "../lib/market";

// The ranking and its row cap live in `lib/symbol-rank.ts` now, because the
// screener's Add row ranks the same way; re-exported so this stays their home
// for everything that already imported them from here.
export { MAX_ROWS, rankSymbols };


/** Anything the Tab trap is allowed to land on. */
const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function SymbolDialog({
  id,
  initialQuery,
  selectSeed,
  keeps,
  onPick,
  onClose,
}: {
  id?: string;
  initialQuery: string;
  /** Select the seed on open, so the first keystroke replaces it. */
  selectSeed: boolean;
  /** The symbol the target chart keeps if this closes unpicked — for the refusal. */
  keeps?: string;
  onPick: (symbol: string) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState(initialQuery);
  /** Why Enter did not chart what was typed, or `null`. Cleared on the next edit. */
  const [refusal, setRefusal] = useState<string | null>(null);
  const [active, setActive] = useState(0);
  /**
   * Where the caret is, so the list can rank on the **operand** being typed
   * rather than on the whole expression.
   *
   * Typing `AAPL/SP` must list SPY; ranking the whole string finds nothing and
   * the feature is unusable. Tracked in state rather than read from the input
   * at render because the ranking is a `useMemo` and has to re-run when the
   * caret moves without the text changing — an arrow key or a click.
   */
  const [caret, setCaret] = useState(initialQuery.length);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const listId = useId();

  // ── track lists ────────────────────────────────────────────────────────────
  //
  // The same star and the same popover as the screener grid, because this is the
  // other surface where somebody has a symbol in hand. It is also the one that
  // mixes markets by design (D40), so it is where a list picks up an `AAPL` and
  // a `RELIANCE.NS` without anything having to ask which market was meant — the
  // suffix already said.
  const { lists: trackLists, enabled: trackEnabled } = useTrackLists();
  const resolveToken = useTokenResolver();
  const [listMenu, setListMenu] = useState<{ symbol: string; anchor: DOMRect } | null>(null);
  const [newList, setNewList] = useState<{ symbols: string[] } | null>(null);
  const [newListError, setNewListError] = useState<string | null>(null);
  const [newListBusy, setNewListBusy] = useState(false);

  const colourOf = (list: TrackList) =>
    resolveToken(colourToken(list.colour)) ?? "var(--qf-accent)";

  /**
   * The price on screen, when there is one.
   *
   * The Price column's own number, so "close at the time of add" is the figure
   * the user was looking at. A row with no price — a symbol the universe has
   * never quoted — sends nothing rather than a zero.
   */
  const closeOf = (row: SymbolRow): Record<string, number> =>
    typeof row.price === "number" && Number.isFinite(row.price) && row.price > 0
      ? { [row.symbol.toUpperCase()]: row.price }
      : {};

  // What the list ranks on: the token under the caret. For a query with no
  // operator that is the whole thing, so nothing about single-symbol search
  // changes.
  const token = activeToken(query, caret);
  const needle = token.value.trim();

  /**
   * The universe, asked for on mount.
   *
   * `true` because a dialog is the one place that certainly needs it — unlike
   * the toolbar box, which wants the index only if it happens to be there.
   * `undefined` until it lands, which is a state this renders rather than
   * waits out.
   */
  const rows = useSymbolIndex(true);

  /**
   * An empty query lists the head of the universe rather than nothing.
   *
   * `rankSymbols` answers `[]` for an empty needle and must keep doing so — it
   * is what closes the list on the degraded paths. Browsing is a *dialog*
   * concern, and the rows arrive market-cap descending, so the first `MAX_ROWS`
   * are the largest companies on the exchange: a genuinely useful opening
   * screen for the cost of a `slice`.
   */
  const matches = useMemo(() => {
    if (!rows) return [];
    return needle ? rankSymbols(rows, needle, MAX_ROWS) : rows.slice(0, MAX_ROWS);
  }, [rows, needle]);

  // Focus the query field on open.
  //
  // Where the caret lands depends on how the dialog was opened, and the two
  // cases want opposite things. A **typed letter** puts the caret after it, so
  // the second letter follows the first instead of landing in front of it. A
  // **click**, which seeds the symbol already charted, selects it — the user is
  // then one keystroke from replacing it and one Backspace from clearing it,
  // without the box first having to be emptied by hand.
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    if (selectSeed) el.select();
    else el.setSelectionRange(el.value.length, el.value.length);
  }, [selectSeed]);

  // A keyboard-driven highlight has to drag the viewport with it, or ArrowDown
  // walks the selection out of sight. `scrollIntoView` is absent in jsdom.
  useLayoutEffect(() => {
    if (active < 0) return;
    const el = listRef.current?.children[active] as HTMLElement | undefined;
    el?.scrollIntoView?.({ block: "nearest" });
  }, [active]);

  /**
   * Keep Tab inside the panel.
   *
   * Wrapping at the ends rather than pinning focus to the input: the panel has a
   * close button, and a trap that refuses to move at all is indistinguishable
   * from a broken keyboard. Focus that has somehow escaped the panel is pulled
   * back to the first control, which is the case a two-element trap otherwise
   * misses.
   */
  const trapTab = (event: React.KeyboardEvent) => {
    const panel = panelRef.current;
    if (!panel) return;
    const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;

    const current = document.activeElement;
    if (!panel.contains(current)) {
      event.preventDefault();
      first.focus();
      return;
    }
    if (event.shiftKey ? current === first : current === last) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    }
  };

  /** Is this symbol one we know? Either spelling — see `symbolResolver`. */
  const holds = symbolResolver(rows);

  /** Whether the box currently holds an expression rather than one ticker. */
  const building = isComposite(query, holds);

  /** Put the caret where the model says it is, once React has drawn the text. */
  const restoreCaret = (position: number) => {
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(position, position);
    });
  };

  /**
   * Commit the whole expression. Never empty.
   *
   * Spelled by `committedSymbol` — an expression without whitespace and
   * uppercased, a plain ticker only trimmed — which the command search's Go to
   * shares, so the two cannot chart one ratio under two spellings.
   */
  const commit = (text: string) => {
    const value = committedSymbol(text);
    if (!value) return;
    // No symbol service at all — a build with no backend. There is no index to
    // come, and no plan to keep anybody inside, so what was typed is charted.
    if (!symbolsConfigured) {
      onPick(value);
      return;
    }
    // **Only a symbol this account may chart.** The server serves whatever a
    // request names, so this is the check (`lib/symbol-access.ts`).
    if (!rows || !holds) {
      setRefusal("Symbols are still loading — press Enter again in a moment.");
      return;
    }
    const leg = firstOutside(value, holds);
    if (leg === null) {
      onPick(value);
      return;
    }
    setRefusal(refusalMessage(value, leg, undefined, keeps));
    // Then the precise sentence, once the unnarrowed name lookup has said
    // whether the symbol exists at all. Only if the box has not moved on.
    const asked = query;
    void refusalFor(value, leg, keeps).then((sentence) => {
      if (inputRef.current?.value === asked) setRefusal(sentence);
    });
  };

  /**
   * A row was chosen — by click, or by Enter with it highlighted.
   *
   * **It replaces the token under the caret**, rather than becoming the whole
   * query. That is what makes the operator buttons usable: after `AAPL/`, the
   * pick fills the right-hand operand instead of throwing away the left. The
   * reference implementation does the same (`ReplaceActiveTokenWithMatch`).
   *
   * The dialog **stays open while an expression is being built** — its
   * `blnRatioMode` — because there is another operand to choose. A plain
   * single-symbol query commits and closes exactly as it always did.
   */
  const choose = (symbol: string) => {
    const next = replaceActiveToken(query, caret, symbol);
    if (!building) {
      commit(next.text);
      return;
    }
    setQuery(next.text);
    setCaret(next.caret);
    setActive(0);
    restoreCaret(next.caret);
  };

  /**
   * Append an operator, the way the reference's four buttons do.
   *
   * A trailing operator is **replaced** rather than added to: `A/` then `-`
   * means the user changed their mind, and `A/-` parses as nothing at all.
   */
  const addOperator = (op: string) => {
    const base = query.trimEnd();
    const next = (/[+\-*/]$/.test(base) ? base.slice(0, -1) : base) + op;
    setQuery(next);
    setCaret(next.length);
    setActive(0);
    restoreCaret(next.length);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setActive((i) => Math.min(i + 1, matches.length - 1));
        return;
      case "ArrowUp":
        event.preventDefault();
        // -1 is "nothing highlighted", so arrowing off the top returns the user
        // to their own text rather than wrapping them into the bottom of a list
        // they were trying to leave.
        setActive((i) => Math.max(i - 1, -1));
        return;
      case "Enter": {
        event.preventDefault();
        // A highlighted row is substituted into the active token first, then
        // the **whole expression** is committed — so Enter on `AAPL/SP` with
        // SPY highlighted charts `AAPL/SPY`, not `SPY`.
        const row = matches[active];
        commit(row ? replaceActiveToken(query, caret, row.symbol).text : query);
        return;
      }
      case "Escape":
        event.preventDefault();
        // Nothing above may also act on this. A modal Escape that bubbles is
        // how one keypress closes the dialog *and* whatever is behind it.
        event.stopPropagation();
        onClose();
        return;
      case "Tab":
        trapTab(event);
        return;
      default:
        return;
    }
  };

  const lower = needle.toLowerCase();

  return (
    <div
      style={backdropStyle}
      // mousedown, not click: a press that starts on a row and drifts onto the
      // backdrop would otherwise dismiss the dialog instead of picking.
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        id={id}
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={onKeyDown}
        style={panelStyle}
      >
        <div style={headerStyle}>
          <h2 id={titleId} style={titleStyle}>
            Search for Symbol
          </h2>
          <button type="button" aria-label="Close" onClick={onClose} style={closeStyle}>
            ×
          </button>
        </div>

        <div style={queryRowStyle}>
          {/* Clear, to the left of the field.
              *
              * Selecting the seed already makes "type over it" the fast path, so
              * this is for the other intent: emptying the box to browse the
              * universe from the top. Rendered `hidden` rather than unmounted
              * when there is nothing to clear, so the field does not shift
              * sideways as the query empties — a control that moves while you
              * type is worse than one that is briefly inert. */}
          <button
            type="button"
            aria-label="Clear search"
            title="Clear"
            onClick={() => {
              setQuery("");
              setActive(0);
              inputRef.current?.focus();
            }}
            style={{ ...clearStyle, visibility: query ? "visible" : "hidden" }}
          >
            ×
          </button>
        <input
          ref={inputRef}
          value={query}
          role="combobox"
          aria-expanded
          aria-controls={listId}
          aria-autocomplete="list"
          // Named separately from the dialog's own title, which is the same
          // words: an accessible name has to identify one control, not two.
          aria-label="Ticker or company name"
          aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
          autoComplete="off"
          spellCheck={false}
          placeholder="Ticker or company name"
          onChange={(event) => {
            setQuery(event.target.value);
            setRefusal(null);
            setCaret(event.target.selectionStart ?? event.target.value.length);
            // Back to the top on every edit: the row under the old index is a
            // different company now, and Enter must never commit one the user
            // has not looked at.
            setActive(0);
          }}
          // The caret moves without the text changing — a click, an arrow key,
          // a drag — and the list ranks on the token it sits in, so every one
          // of those has to re-rank. `onSelect` fires for all three.
          onSelect={(event) => {
            const el = event.target as HTMLInputElement;
            setCaret(el.selectionStart ?? el.value.length);
          }}
          style={queryStyle}
          aria-describedby={refusal ? `${listId}-refusal` : undefined}
        />

        {/* The four operators, as the reference app has them.
            *
            * After Clear and the field rather than before, because they act on
            * what has been typed: the reading is "AAPL, divided by…". Each one
            * appends and returns focus to the box, so the next keystroke lands
            * where the user is looking.
            *
            * `−` and `×` are drawn, `-` and `*` are inserted. The typographic
            * forms are what a person recognises as operators at 13px; the
            * ASCII ones are what the grammar accepts. */}
        {/* **Off in the brand means a composite cannot be built here.** The
            grammar still parses one that arrives in a saved workspace — a
            stored `AAPL/SPY` must keep working — but the four buttons that
            make one typable are the feature, and they go with it. */}
        {available("ratioCharts") &&
          OPERATOR_BUTTONS.map(([op, glyph, label]) => (
          <button
            key={op}
            type="button"
            aria-label={label}
            title={label}
            onClick={() => addOperator(op)}
            style={operatorStyle}
          >
            {glyph}
          </button>
        ))}
        </div>

        {/* Why Enter did not chart what was typed — directly under the box it
            is about, so it is read before the list. `role="alert"` so a screen
            reader hears it without moving focus off the field. */}
        {refusal && (
          <div id={`${listId}-refusal`} role="alert" style={refusalStyle}>
            {refusal}
          </div>
        )}

        {/* Headers are decoration over a listbox, not a grid — the control is a
            single-choice list and announcing it as a table would make a screen
            reader user navigate cells to reach one. */}
        <div style={headRowStyle} aria-hidden>
          <span>Symbol</span>
          <span>Name</span>
          <span>Sector</span>
          <span>Industry</span>
          <span style={numHeadStyle}>Market Cap</span>
          <span style={numHeadStyle}>Price</span>
        </div>

        <div ref={listRef} id={listId} role="listbox" aria-label="Symbols" style={listStyle}>
          {matches.map((row, index) => (
            <div
              key={row.symbol}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === active}
              // Selecting must not blur the query field first: a blur would let
              // React re-render and move the row out from under the click.
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setActive(index)}
              onClick={() => choose(row.symbol)}
              style={{
                ...rowStyle,
                background:
                  index === active
                    ? "var(--qf-selection)"
                    : index % 2 === 1
                      ? "var(--qf-row-alt)"
                      : "transparent",
              }}
            >
              <span style={symbolCellStyle}>
                {/* The **bare** ticker when a badge follows it: `RELIANCE.NS`
                    beside an `NSE` badge says the same thing twice, in a column
                    that has room for neither. The row's value stays the full
                    symbol — only the rendering drops the suffix. */}
                <span style={symbolTextStyle}>
                  <Highlight
                    text={marketOf(row).code === "US" ? row.symbol : bareSymbol(row.symbol)}
                    needle={lower}
                  />
                </span>
                {/* Badged rather than given a column: the grid template is a
                    fixed set of fractions, so a seventh column resizes every
                    other one. The market belongs beside the symbol anyway — it
                    is part of the thing's identity, not a separate attribute.
                    Only non-default markets are badged, so the US list looks
                    exactly as it did. */}
                {marketOf(row).code !== "US" && (
                  <span style={marketBadgeStyle}>{marketOf(row).label}</span>
                )}
                {/* **Inside the symbol cell, not a seventh column** — for the
                    reason the badge above gives: the row is a fixed set of
                    fractions, so another column resizes every other one. It
                    rides at the end of the cell and only shows when the row is
                    active or the symbol is already on a list, so a search that
                    has nothing to do with lists looks exactly as it did.

                    The click is swallowed both ways: `onMouseDown` because the
                    row's own handler prevents default to keep the list stable,
                    and `onClick` because clicking a row *charts* the symbol —
                    without this, starring one would also close the dialog. */}
                {trackEnabled && (() => {
                  const on = listsFor(row.symbol);
                  if (on.length === 0 && index !== active) return null;
                  return (
                    <span
                      role="button"
                      aria-label={`Track lists for ${row.symbol}`}
                      title="Add to a list"
                      style={{
                        ...starStyle,
                        color: on.length ? colourOf(on[0]!) : "var(--qf-text-muted)",
                        opacity: on.length ? 1 : 0.65,
                      }}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        const anchor = (
                          event.currentTarget as HTMLElement
                        ).getBoundingClientRect();
                        const target = defaultList();
                        if (event.altKey && target) {
                          void toggleSymbols(target.id, [row.symbol], closeOf(row)).catch(
                            () => {},
                          );
                          return;
                        }
                        setListMenu({ symbol: row.symbol, anchor });
                      }}
                    >
                      {on.length ? "★" : "☆"}
                    </span>
                  );
                })()}
              </span>
              <span style={cellStyle} title={row.name ?? undefined}>
                {row.name ? <Highlight text={row.name} needle={lower} /> : ""}
              </span>
              {/* An ETF has no sector, so the column would just be blank for
                  one; naming the instrument there keeps the distinction the old
                  list showed as a badge. */}
              <span style={cellStyle}>{row.sector ?? (row.isEtf ? "ETF" : "")}</span>
              <span style={cellStyle} title={row.industry ?? undefined}>
                {row.industry ?? ""}
              </span>
              {/* Formatted from the **row's own** market, not the browser's
                  locale: an Indian cap reads in lakh and crore, and showing
                  `1.8T` where a user expects `17.7 lakh crore` is a
                  wrong-looking number rather than a differently-formatted one.
                  D40: format per row, never sort across rows. */}
              <span style={numCellStyle}>{formatCap(row.marketCap, marketOf(row))}</span>
              <span style={numCellStyle}>{formatPrice(row.price, marketOf(row))}</span>
            </div>
          ))}

          {matches.length === 0 && rows !== undefined && (
            <div style={emptyStyle}>
              No match for “{needle}”.
            </div>
          )}
        </div>

        <div style={footerStyle}>
          {rows === undefined
            ? // Said out loud rather than left blank: the dialog opened before
              // the universe arrived, and "no matches" would be a lie.
              "Loading symbols… you can type while they load."
            : building
              ? // While an expression is being built the match count is the
                // wrong thing to report — what the user needs to know is
                // whether every leg names something we have.
                expressionNote(query, rows)
              : footnote(needle, matches.length, rows.length)}
        </div>
      </div>

      {listMenu && available("tracklists") && (
        <TrackListMenu
          anchor={listMenu.anchor}
          symbols={[listMenu.symbol]}
          lists={trackLists}
          colourOf={colourOf}
          onToggle={(list) => {
            const row = matches.find((m) => m.symbol === listMenu.symbol);
            void toggleSymbols(
              list.id,
              [listMenu.symbol],
              row ? closeOf(row) : undefined,
            ).catch(() => {});
          }}
          onCreate={() => {
            setNewListError(null);
            setNewList({ symbols: [listMenu.symbol] });
            setListMenu(null);
          }}
          onRemoveAll={() => {
            void removeFromAll([listMenu.symbol]).catch(() => {});
            setListMenu(null);
          }}
          onClose={() => setListMenu(null)}
        />
      )}

      {newList && (
        <NewListDialog
          lists={trackLists}
          resolve={resolveToken}
          seedCount={newList.symbols.length}
          error={newListError}
          busy={newListBusy}
          onCreate={(spec) => {
            setNewListBusy(true);
            setNewListError(null);
            const row = matches.find((m) => m.symbol === newList.symbols[0]);
            void createList({
              id: mintUlid(),
              name: spec.name,
              hotKey: spec.hotKey,
              colour: spec.colour,
              isDefault: spec.isDefault,
              symbols: newList.symbols,
              closes: row ? closeOf(row) : undefined,
            })
              .then(() => setNewList(null))
              .catch((err: unknown) =>
                setNewListError(err instanceof Error ? err.message : String(err)),
              )
              .finally(() => setNewListBusy(false));
          }}
          onClose={() => setNewList(null)}
        />
      )}
    </div>
  );
}

/**
 * Whether the expression in the box can be charted, and what is wrong if not.
 *
 * The match count means nothing here — the list is ranking one operand of
 * several. What a user building `AAPL/SPX` needs to be told is that `SPX` is
 * not a symbol we have, **before** pressing Enter and getting an empty chart.
 *
 * An unfinished expression is not an error: `AAPL/` is what every ratio looks
 * like halfway through typing it, so it prompts rather than complains.
 */
export function expressionNote(query: string, rows: readonly SymbolRow[]): string {
  const parsed = parseComposite(query);
  if (!parsed) return "Finish the expression — every operator needs a symbol either side.";
  const known = new Set(rows.map((r) => r.symbol));
  const missing = parsed.operands.filter((operand) => !known.has(operand));
  if (missing.length === 0) {
    return `${parsed.operands.join(" and ")} — press Enter to chart it.`;
  }
  const list = missing.join(", ");
  return missing.length === 1
    ? `${list} isn't available — pick a symbol from the list.`
    : `${list} aren't available — pick symbols from the list.`;
}

/**
 * What the list is not showing, said out loud.
 *
 * A list that stops at `MAX_ROWS` without saying so reads as "that symbol does
 * not exist", which is the one conclusion it must not lead a user to.
 */
function footnote(needle: string, shown: number, total: number): string {
  if (!needle) {
    return total > shown
      ? `The ${shown} largest by market cap — type to search all ${total.toLocaleString()}.`
      : `${total.toLocaleString()} symbols.`;
  }
  if (shown >= MAX_ROWS) return `First ${MAX_ROWS} matches — keep typing to narrow.`;
  return `${shown.toLocaleString()} ${shown === 1 ? "match" : "matches"}.`;
}

/**
 * `4862365750000` as `4.86T`, because the raw number is unreadable in a column
 * five characters wide.
 *
 * **This duplicates `formatNotation` in `@qufolio/screener-grid`, deliberately.**
 * That package exports one barrel and nothing else, so importing its one number
 * formatter drags AG Grid in behind it — measured at ~3s added to this file's
 * test run, since every `vi.resetModules()` re-evaluates the whole island. The
 * grid's version also carries policy this has no use for (fixed K/M/B units for
 * a screener column the user configures); the shared part is the single `Intl`
 * call below. If screener-grid ever exposes `./lib/notation` as a subpath, this
 * should become an import.
 *
 * `Intl` rather than a hand-rolled ladder for the same reason its version gives:
 * it is locale-aware — lakh and crore in `en-IN`, not just K/M/B/T.
 */
/**
 * Which market a row is in.
 *
 * Prefers the server's own answer and falls back to the suffix, which is the
 * rule `market_for` uses — so a row that predates the field badges correctly
 * rather than silently reading as US.
 */
function marketOf(row: SymbolRow): MarketInfo {
  return row.market ? marketByCode(row.market) : marketForSymbol(row.symbol);
}

const marketBadgeStyle: React.CSSProperties = {
  marginLeft: 6,
  padding: "0 4px",
  borderRadius: 3,
  fontSize: "0.68em",
  fontWeight: 600,
  letterSpacing: "0.04em",
  color: "var(--text-muted, #888)",
  border: "1px solid var(--grid, #444)",
  verticalAlign: "middle",
}

/**
 * The track-list star, riding at the end of the symbol cell.
 *
 * `marginLeft: auto` so it sits at the cell's right edge whatever the ticker's
 * width — the alternative, a fixed offset, puts it in a different place on every
 * row and the column stops reading as a column.
 */
const starStyle: React.CSSProperties = {
  marginLeft: "auto",
  paddingLeft: 6,
  fontSize: "0.95em",
  lineHeight: 1,
  cursor: "pointer",
  flex: "none",
}

/**
 * The matched run, marked in the accent colour.
 *
 * Colour and weight only — no background chip. A highlight behind text has to
 * guarantee contrast against whatever the theme resolved, and there are four
 * themes with no token that means "readable on accent".
 */
function Highlight({ text, needle }: { text: string; needle: string }) {
  const at = needle ? text.toLowerCase().indexOf(needle) : -1;
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <span style={{ color: "var(--qf-accent)" }}>{text.slice(at, at + needle.length)}</span>
      {text.slice(at + needle.length)}
    </>
  );
}

/**
 * One template for the header row and every body row, so the two cannot drift.
 *
 * Fractional units with a floor: Name and Industry are the long ones and take
 * the slack, while Symbol / Market Cap / Price are fixed because a number column
 * that changes width as you type is unreadable.
 *
 * Symbol went 5.5rem -> 7.5rem when the market badge arrived. The badge is
 * inside that cell, and at the old width `RELIANCE` plus `NSE` was clipped —
 * a badge you cannot see is worse than none, because the row then reads as a
 * US symbol.
 */
const GRID_COLUMNS = "7.5rem minmax(9rem, 1.7fr) minmax(6rem, 1fr) minmax(8rem, 1.4fr) 6rem 5.5rem";

const backdropStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  // Above `GroupBar`'s menus (1000), which is the highest thing in the app.
  zIndex: 2000,
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  paddingTop: "8vh",
  // The one literal colour here: a scrim darkens whatever is behind it in every
  // theme, and the token set has no name for it — same call the box-shadows make.
  background: "rgba(0, 0, 0, 0.5)",
};

const panelStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  width: "min(920px, calc(100vw - 2rem))",
  maxHeight: "min(640px, calc(100vh - 12vh))",
  background: "var(--qf-surface)",
  border: "1px solid var(--qf-border)",
  borderRadius: 8,
  boxShadow: "0 16px 48px rgba(0,0,0,0.45)",
  overflow: "hidden",
};

const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0.6rem 0.9rem",
  borderBottom: "1px solid var(--qf-border)",
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  font: "inherit",
  fontSize: "0.95rem",
  fontWeight: 600,
  color: "var(--qf-text)",
};

const closeStyle: React.CSSProperties = {
  font: "inherit",
  fontSize: "1.1rem",
  lineHeight: 1,
  padding: "0 0.35rem",
  border: 0,
  borderRadius: 4,
  background: "transparent",
  color: "var(--qf-text-secondary)",
  cursor: "pointer",
};

/** Holds the clear button and the query field on one line. */
const queryRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.4rem",
  margin: "0.75rem 0.9rem",
};

/**
 * The four operators: what is inserted, what is drawn, and what it is called.
 *
 * The glyph and the value differ on two of them on purpose. `−` (U+2212) and
 * `×` (U+00D7) are what a person reads as minus and times at this size — a
 * hyphen is a word-joiner and an asterisk floats above the baseline — while the
 * grammar accepts only the ASCII forms. Nothing but this table knows about the
 * difference, so a button can never insert a character the parser refuses.
 */
const OPERATOR_BUTTONS: readonly (readonly [string, string, string])[] = [
  ["+", "+", "Add"],
  ["-", "−", "Subtract"],
  ["*", "×", "Multiply"],
  ["/", "/", "Divide — a ratio"],
];

const operatorStyle: React.CSSProperties = {
  flex: "0 0 auto",
  width: 28,
  height: 28,
  lineHeight: "26px",
  fontSize: "1rem",
  padding: 0,
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text)",
  border: "1px solid var(--qf-border)",
  borderRadius: 6,
  cursor: "pointer",
};

const clearStyle: React.CSSProperties = {
  flex: "0 0 auto",
  width: 28,
  height: 28,
  lineHeight: "26px",
  fontSize: "1.1rem",
  padding: 0,
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text-secondary)",
  border: "1px solid var(--qf-border)",
  borderRadius: 6,
  cursor: "pointer",
};

const queryStyle: React.CSSProperties = {
  // The row owns the margins now; the field owns the remaining width.
  flex: "1 1 auto",
  minWidth: 0,
  padding: "0.5rem 0.6rem",
  font: "inherit",
  fontSize: "1rem",
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text)",
  border: "1px solid var(--qf-border)",
  borderRadius: 6,
  outline: "none",
};

const headRowStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: GRID_COLUMNS,
  gap: "0.6rem",
  padding: "0.3rem 0.9rem",
  borderTop: "1px solid var(--qf-border)",
  borderBottom: "1px solid var(--qf-border)",
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text-secondary)",
  fontSize: "0.7rem",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const numHeadStyle: React.CSSProperties = { textAlign: "right" };

const listStyle: React.CSSProperties = {
  flex: "1 1 auto",
  minHeight: 0,
  overflowY: "auto",
};

const rowStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: GRID_COLUMNS,
  gap: "0.6rem",
  alignItems: "center",
  padding: "0.25rem 0.9rem",
  borderBottom: "1px solid var(--qf-border)",
  cursor: "pointer",
  fontSize: "0.78rem",
  color: "var(--qf-text)",
};

/**
 * Every cell truncates. `minWidth: 0` is what makes that work: a grid track's
 * default `min-content` floor lets a long company name push the row wider than
 * the panel instead of clipping, and `overflow: hidden` alone will not stop it.
 */
const cellStyle: React.CSSProperties = {
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  color: "var(--qf-text-secondary)",
};

/**
 * The identity cell: ticker, an optional market badge, an optional star.
 *
 * **A flex row, unlike every other cell**, because the star has to sit at the
 * cell's right edge on every row — inline it lands wherever the ticker happens
 * to end, and a column of stars at six different offsets stops reading as a
 * column. The truncation the other cells get from `cellStyle` moves onto the
 * ticker's own span (`symbolTextStyle`), so a long ticker still ellipses
 * instead of pushing the star out of the cell.
 */
const symbolCellStyle: React.CSSProperties = {
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontWeight: 700,
  color: "var(--qf-text)",
};

const symbolTextStyle: React.CSSProperties = {
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const numCellStyle: React.CSSProperties = {
  ...cellStyle,
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
};

const emptyStyle: React.CSSProperties = {
  padding: "1.25rem 0.9rem",
  textAlign: "center",
  fontSize: "0.8rem",
  color: "var(--qf-text-muted)",
};

/** The refusal under the box: the same row padding as the list, in the refusal colour. */
const refusalStyle: React.CSSProperties = {
  padding: "0.35rem 0.9rem",
  fontSize: "0.8rem",
  color: "var(--qf-negative)",
  borderBottom: "1px solid var(--qf-border)",
};

const footerStyle: React.CSSProperties = {
  padding: "0.4rem 0.9rem",
  borderTop: "1px solid var(--qf-border)",
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text-muted)",
  fontSize: "0.7rem",
};
